import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u6_x2_taupunktwaechter';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einführung',
    body: 'Taupunktwächter, wie der WK01, sind Sicherheitseinrichtungen, die Kondenswasserbildung an kalten Oberflächen (z. B. Kühlrohren) verhindern. Sie detektieren eine drohende Betauung und ermöglichen es der Regelung, rechtzeitig Gegenmaßnahmen einzuleiten.'
  },
  {
    heading: 'Funktionsweise WK01',
    body: 'Der WK01 besitzt ein Sensorelement, das direkt auf der zu überwachenden kalten Oberfläche montiert wird. Nähert sich die relative Feuchte an dieser Oberfläche dem Taupunkt (ca. 90–95% r. F.), ändert der Sensor seine elektrischen Eigenschaften. Die integrierte Elektronik wertet dies aus und schaltet ein Relais, bevor es zu sichtbarer Kondensation kommt.'
  },
  {
    heading: 'Elektrischer Anschluss',
    body: 'Spannungsversorgung: typ. 24V AC/DC (exakte Klemmen gemäß Datenblatt).\n\nAnschluss Schaltkontakt an die DDC: Der potentialfreie Relaisausgang (Wechsler) meldet Betauungsgefahr. Üblich sind die Klemmen COM und NO (Schließer) oder NC (Öffner) – je nach gewünschter Logik in der DDC. Ein Schließerkontakt (NO) schließt bei Detektion und meldet dies an einen DI der DDC.'
  },
  {
    heading: 'Sicherheitsfunktion & Regelungseingriff',
    body: 'Bei Betauungsgefahr greift die GA ein: 1) Kaltwasservorlauftemperatur anheben. 2) Kühlleistung reduzieren/abschalten (Ventile drosseln, Maschine modulieren). 3) Alarmierung an die Leitstelle. So werden Feuchteschäden vermieden.'
  },
  {
    heading: 'Fazit',
    body: 'Die korrekte Montage an der kältesten Stelle und die richtige Einbindung des WK01 in die Kälteregelung sind entscheidend, um Kondensatschäden zu vermeiden und einen sicheren Betrieb zu gewährleisten.'
  }
];

const images = [
  { id: 'img_tpw_wk01_beispiel', label: 'Taupunktwächter WK01', src: `${IMAGE_BASE}geraet_sicherheit_wk01.jpg`, alt: 'Beispiel eines Taupunktwächters WK01' },
  { id: 'img_tpw_schaltplan_anschluss', label: 'Schaltplan Anschluss', src: `${IMAGE_BASE}schaltplan_tpw_epl.PNG`, alt: 'Schaltplanausschnitt: Anschluss des Taupunktwächters WK01' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.6.2 Taupunktwächter', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u6_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


